import { useState, useEffect, useRef, useCallback } from 'react';
import { X as XIcon, Search, Upload, Image as ImageIcon, Video as VideoIcon, FileText, CheckCircle2, Loader2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const MAX_SIZE_BYTES = 50 * 1024 * 1024;
const ALLOWED_PREVIEW = ['image/', 'video/'];

function streamUrl(dateipfad) {
  if (!dateipfad) return '';
  return `${API}/api/prozesse/medien/stream/${encodeURIComponent(dateipfad)}`;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function MediumThumb({ medium }) {
  if (medium.dateityp === 'image') {
    return <img src={streamUrl(medium.dateipfad)} alt="" className="w-full h-full object-cover" />;
  }
  if (medium.dateityp === 'video') {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center relative">
        <video src={streamUrl(medium.dateipfad)} preload="metadata" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
            <div className="w-0 h-0 border-y-[6px] border-y-transparent border-l-[10px] border-l-[#1A1A2E] ml-0.5" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full h-full bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center">
      <FileText className="w-8 h-8 text-[var(--text-tertiary,#6B7280)] opacity-50" strokeWidth={1.5} />
    </div>
  );
}

export default function MediumPickerModal({ open, onClose, prozessId, schrittId, alreadyLinkedIds = [], onLinked, t }) {
  const [tab, setTab] = useState('library');
  const [library, setLibrary] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  // Upload state
  const [uploadFiles, setUploadFiles] = useState([]); // { file, status, progress, error, mediumId }
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const tt = useCallback((k, fb) => (t ? (t(k) || fb) : fb), [t]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setUploadFiles([]);
    setError(null);
    setSearch('');
    setTab('library');
  }, [open]);

  const loadLibrary = useCallback(async () => {
    if (!prozessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/prozesse/${prozessId}/medien`, { credentials: 'include' });
      const data = await res.json();
      setLibrary(data.medien || []);
    } catch {
      setError(tt('prozesse.medien.load_failed', 'Bibliothek konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [prozessId, tt]);

  useEffect(() => { if (open) loadLibrary(); }, [open, loadLibrary]);

  if (!open) return null;

  const linkedSet = new Set(alreadyLinkedIds);
  const filtered = library.filter(m => !search || m.dateiname.toLowerCase().includes(search.toLowerCase()));

  const toggleSelect = (id) => {
    if (linkedSet.has(id)) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selected.size === 0) return;
    setLinking(true);
    setError(null);
    try {
      const ids = [...selected];
      for (const mid of ids) {
        const res = await fetch(`${API}/api/prozesse/${prozessId}/schritte/${schrittId}/medien`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ medium_id: mid }),
        });
        if (!res.ok && res.status !== 409) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${res.status}`);
        }
      }
      onLinked?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLinking(false);
    }
  };

  const acceptFiles = (fileList) => {
    const arr = Array.from(fileList);
    const next = arr.map(f => {
      const ok = ALLOWED_PREVIEW.some(p => f.type.startsWith(p)) || f.type === 'application/pdf';
      const tooBig = f.size > MAX_SIZE_BYTES;
      return {
        file: f,
        status: ok && !tooBig ? 'pending' : 'invalid',
        progress: 0,
        error: tooBig ? tt('prozesse.medien.upload_too_large', 'Datei zu groß (max 50 MB)') : (!ok ? tt('prozesse.medien.upload_invalid_type', 'Dateityp nicht unterstützt') : null),
        mediumId: null,
      };
    });
    setUploadFiles(prev => [...prev, ...next]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) acceptFiles(e.dataTransfer.files);
  };

  const startUploads = async () => {
    const queue = uploadFiles.map((u, idx) => ({ ...u, idx })).filter(u => u.status === 'pending');
    for (const item of queue) {
      // mark uploading
      setUploadFiles(prev => prev.map((u, i) => i === item.idx ? { ...u, status: 'uploading', progress: 0 } : u));
      const fd = new FormData();
      fd.append('file', item.file);
      try {
        const res = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API}/api/prozesse/${prozessId}/schritte/${schrittId}/medien/upload`);
          xhr.withCredentials = true;
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) {
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setUploadFiles(prev => prev.map((u, i) => i === item.idx ? { ...u, progress: pct } : u));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch { resolve({}); }
            } else {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(fd);
        });
        setUploadFiles(prev => prev.map((u, i) => i === item.idx ? { ...u, status: 'done', progress: 100, mediumId: res?.medium?.id || null } : u));
      } catch (e) {
        setUploadFiles(prev => prev.map((u, i) => i === item.idx ? { ...u, status: 'error', error: e.message } : u));
      }
    }
    onLinked?.();
  };

  const validUploads = uploadFiles.filter(u => u.status !== 'invalid');
  const pendingUploads = uploadFiles.filter(u => u.status === 'pending');
  const allDone = validUploads.length > 0 && validUploads.every(u => u.status === 'done' || u.status === 'error');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">
            {tt('prozesse.medien.picker_title', 'Medium auswählen')}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)]">
            <XIcon className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
          </button>
        </div>

        {/* Tab Bar */}
        <div className="px-5 pt-4">
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)] inline-flex">
            <button
              onClick={() => setTab('library')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${tab === 'library' ? 'bg-white shadow-sm font-medium text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-secondary,#4B5563)]'}`}
            >
              {tt('prozesse.medien.picker_tab_library', 'Aus Bibliothek')}
            </button>
            <button
              onClick={() => setTab('upload')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${tab === 'upload' ? 'bg-white shadow-sm font-medium text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-secondary,#4B5563)]'}`}
            >
              {tt('prozesse.medien.picker_tab_upload', 'Neu hochladen')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">{error}</div>
          )}

          {tab === 'library' && (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={tt('prozesse.medien.picker_search_placeholder', 'Dateiname suchen...')}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
                />
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[var(--text-tertiary,#6B7280)]" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">
                  <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="text-sm">{tt('prozesse.medien.library_empty', 'Keine Medien in der Bibliothek')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filtered.map(m => {
                    const isLinked = linkedSet.has(m.id);
                    const isSelected = selected.has(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={isLinked}
                        onClick={() => toggleSelect(m.id)}
                        className={`group relative rounded-xl overflow-hidden border-2 transition-all text-left ${
                          isLinked ? 'border-[var(--border-card,#E5E7EB)] opacity-50 cursor-not-allowed' :
                          isSelected ? 'border-[#338edc] ring-2 ring-[#338edc]/20' :
                          'border-[var(--border-card,#E5E7EB)] hover:border-[#338edc]/40'
                        }`}
                      >
                        <div className="aspect-square bg-[var(--bg-inset,#F8F9FC)]">
                          <MediumThumb medium={m} />
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium text-[var(--text-primary,#1A1A2E)] truncate">{m.dateiname}</p>
                          <p className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{formatSize(m.dateigroesse)}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#338edc] flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
                          </div>
                        )}
                        {isLinked && (
                          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-lg bg-emerald-500/90 text-white text-[10px] font-medium">
                            {tt('prozesse.medien.picker_already_linked', 'Bereits verknüpft')}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {tab === 'upload' && (
            <>
              <div
                onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                  dragActive ? 'border-[#338edc] bg-[#EBF4FD]' : 'border-[var(--border-card,#E5E7EB)] hover:border-[#338edc]'
                }`}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />
                <p className="text-sm text-[var(--text-secondary,#4B5563)]">
                  {tt('prozesse.medien.upload_dropzone', 'Dateien hierher ziehen oder klicken zum Auswählen')}
                </p>
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1">
                  {tt('prozesse.medien.upload_max_size', 'Max. 50 MB pro Datei')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,video/*,application/pdf"
                  onChange={e => { acceptFiles(e.target.files); e.target.value = ''; }}
                />
              </div>

              {uploadFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadFiles.map((u, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)]">
                      <div className="w-10 h-10 rounded-lg bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center shrink-0">
                        {u.file.type.startsWith('image/') ? <ImageIcon className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} /> :
                         u.file.type.startsWith('video/') ? <VideoIcon className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} /> :
                         <FileText className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" strokeWidth={1.5} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] truncate">{u.file.name}</p>
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{formatSize(u.file.size)}</p>
                        {u.status === 'uploading' && (
                          <div className="mt-1 h-1 rounded-full bg-[var(--bg-inset,#F8F9FC)] overflow-hidden">
                            <div className="h-full bg-[#338edc] transition-all" style={{ width: `${u.progress}%` }} />
                          </div>
                        )}
                        {u.error && <p className="text-xs text-rose-600 mt-0.5">{u.error}</p>}
                      </div>
                      <div className="text-xs text-[var(--text-tertiary,#6B7280)] shrink-0">
                        {u.status === 'pending' && tt('prozesse.medien.upload_status_pending', 'Bereit')}
                        {u.status === 'uploading' && `${u.progress}%`}
                        {u.status === 'done' && (
                          <span className="text-emerald-600 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                            {tt('prozesse.medien.upload_status_done', 'OK')}
                          </span>
                        )}
                        {u.status === 'error' && <span className="text-rose-600">{tt('prozesse.medien.upload_status_error', 'Fehler')}</span>}
                        {u.status === 'invalid' && <span className="text-amber-600">{tt('prozesse.medien.upload_status_invalid', 'Ungültig')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border-card,#E5E7EB)] p-4 flex items-center justify-between">
          <div className="text-sm text-[var(--text-tertiary,#6B7280)]">
            {tab === 'library' && selected.size > 0 && (
              tt('prozesse.medien.picker_select_count', '{{n}} ausgewählt').replace('{{n}}', String(selected.size))
            )}
            {tab === 'upload' && uploadFiles.length > 0 && (
              `${validUploads.filter(u => u.status === 'done').length} / ${validUploads.length} ${tt('prozesse.medien.upload_progress_files', 'Dateien hochgeladen')}`
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]"
            >
              {tt('common.cancel', 'Abbrechen')}
            </button>
            {tab === 'library' ? (
              <button
                onClick={handleConfirm}
                disabled={selected.size === 0 || linking}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40 inline-flex items-center gap-2"
              >
                {linking && <Loader2 className="w-4 h-4 animate-spin" />}
                {tt('prozesse.medien.picker_confirm', 'Verknüpfen')}{selected.size > 0 ? ` (${selected.size})` : ''}
              </button>
            ) : (
              <button
                onClick={allDone ? onClose : startUploads}
                disabled={pendingUploads.length === 0 && !allDone}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40"
              >
                {allDone
                  ? tt('common.done', 'Fertig')
                  : `${tt('prozesse.medien.upload_start', 'Hochladen')}${pendingUploads.length > 0 ? ` (${pendingUploads.length})` : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
